import {
  CallsToAction,
  CallToActionItem,
  Media,
  RetailerAdCreativeTemplate,
} from '@promoboxx/graphql-gateway-types'
import {
  BaseAdMedia,
  embedSocialPreview,
  SocialPreviewAdOptions,
} from '@promoboxx/social-preview'
import { connect, FormikContext } from 'formik'
import { useRef, useMemo, useEffect, useState } from 'react'

import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { usePaidAds } from '@src/lib/usePaidAds'

import { AdPreviewType, adPreviewTypes } from '../lib/adPreviewTypes'
import cloudinary from '../lib/Cloudinary'
import useCombinedPremiumAdData from '../lib/useCombinedPremiumAdData'

import { ActivateCampaignFormValues } from './FPPAP'

type PaidAdSocialPreviewProps = {
  campaignId: string
  type: string
  formik?: FormikContext<ActivateCampaignFormValues>
}

const PaidAdSocialPreview = (props: PaidAdSocialPreviewProps) => {
  const { getRetailerAdCreativeTemplate, getCallsToAction } = usePaidAds()
  const { ensureCurrentRetailerAccount: currentRetailerAccount } =
    useCurrentRetailerAccount()
  const campaignData = useCombinedPremiumAdData(props.campaignId)
  const previewRef = useRef<HTMLDivElement | null>(null)
  const formCreative = props.formik?.values.adCreativeTemplate

  const [
    adCreateTemplateWithReplacementsAndExtraData,
    setAdCreateTemplateWithReplacementsAndExtraData,
  ] = useState<RetailerAdCreativeTemplate | null>(null)
  const [callsToAction, setCallsToAction] = useState<CallsToAction | null>(null)

  const facebookPage = useMemo(() => {
    return currentRetailerAccount.facebook_pages?.find(
      (p) => p.id === String(props.formik?.values.facebookPageId),
    )
  }, [
    currentRetailerAccount.facebook_pages,
    props.formik?.values.facebookPageId,
  ])

  const retailerId = useMemo(() => {
    return (
      currentRetailerAccount.retailers?.find((r) => {
        return r.brand_id === campaignData?.campaign?.brand_id
      })?.id || null
    )
  }, [campaignData?.campaign?.brand_id, currentRetailerAccount.retailers])

  useEffect(() => {
    getCallsToAction().then((response) => {
      setCallsToAction(response)
    })
  }, [getCallsToAction])

  useEffect(() => {
    if (retailerId && campaignData?.adCreativeTemplate?.id) {
      getRetailerAdCreativeTemplate(
        retailerId,
        campaignData?.adCreativeTemplate?.id,
      ).then((response) => {
        setAdCreateTemplateWithReplacementsAndExtraData(response)
      })
    }
  }, [
    campaignData?.adCreativeTemplate?.id,
    retailerId,
    getRetailerAdCreativeTemplate,
  ])

  const socialPreviewOptions: SocialPreviewAdOptions | null = useMemo(() => {
    if (
      !formCreative ||
      !callsToAction ||
      !adCreateTemplateWithReplacementsAndExtraData
    ) {
      return null
    }

    let options: SocialPreviewAdOptions | null = null

    let media
    const video_data =
      adCreateTemplateWithReplacementsAndExtraData?.object_story_spec
        ?.video_data
    const link_data =
      adCreateTemplateWithReplacementsAndExtraData?.object_story_spec?.link_data

    if (formCreative.creative_type === 'carousel') {
      media = formCreative?.creative?.child_attachments?.map(
        (childAttachment, i) => {
          const childAttachmentWithExtraData =
            adCreateTemplateWithReplacementsAndExtraData?.object_story_spec
              ?.link_data?.child_attachments?.[i]
          const mediaType = childAttachment?.media
            ?.media_type as BaseAdMedia['mediaType']
          const mediaObject: Media | undefined | null | any =
            mediaType === 'image'
              ? childAttachment.media
              : {
                  ...childAttachment.media,
                  source_id: childAttachmentWithExtraData?.cloudinary_id,
                }
          const callsToActionKey = childAttachment?.call_to_action
            ?.cta_type as keyof CallsToAction
          const callToActionType = callsToAction?.[
            callsToActionKey
          ] as CallToActionItem
          return {
            mediaType,
            mediaSrc: mediaObject ? cloudinaryUrlWithCrops(mediaObject) : '',
            url: childAttachment?.link?.url || '',
            callToAction:
              (callToActionType
                ? callToActionType.value
                : childAttachment?.call_to_action?.cta_type) || '',
            headline: childAttachmentWithExtraData?.name || '',
            description: childAttachmentWithExtraData?.description || '',
          }
        },
      )
    } else {
      const mediaType = formCreative?.creative?.media
        ?.media_type as BaseAdMedia['mediaType']
      const mediaObject: Media | undefined | null | any =
        mediaType === 'image'
          ? formCreative?.creative?.media
          : {
              ...formCreative?.creative?.media,
              source_id: link_data?.cloudinary_id || video_data?.cloudinary_id,
            }
      const callsToActionKey = formCreative?.creative?.call_to_action
        ?.cta_type as keyof CallsToAction
      const callToActionType = callsToAction?.[
        callsToActionKey
      ] as CallToActionItem

      media = [
        {
          mediaType,
          mediaSrc: mediaObject ? cloudinaryUrlWithCrops(mediaObject) : '',
          url: formCreative.creative?.link?.url || '',
          callToAction:
            (callToActionType
              ? callToActionType.value
              : formCreative?.creative?.call_to_action?.cta_type) || '',
          headline:
            (link_data && link_data.name) ||
            (video_data && video_data.title) ||
            '',
          description:
            (link_data && link_data.description) ||
            (video_data && video_data.link_description) ||
            '',
        },
      ]
    }

    const type =
      (adPreviewTypes[props.type as keyof AdPreviewType]
        ?.socialPreviewType as SocialPreviewAdOptions['type']) || null
    if (type && media) {
      options = {
        type: type as any, //TODO Maybe the types from the social preview need to be updated to not have the 'any' type
        payload: {
          userFullName: facebookPage?.name || '',
          userHandle: facebookPage?.name || '',
          userAvatar: facebookPage?.picture_url || '',
          primaryText:
            (link_data && link_data.message) ||
            (video_data && video_data.message) ||
            '',
          carouselIndex: props.formik?.values.carouselIndex,
          media,
        },
        events: {
          'socialPreview/carouselIndexUpdated': (event: any) => {
            props.formik?.setFieldValue(
              'carouselIndex',
              event.data.payload.carouselIndex,
            )
          },
        },
        align: 'center',
      }

      return options
    } else {
      return null
    }
  }, [
    formCreative,
    adCreateTemplateWithReplacementsAndExtraData,
    facebookPage,
    props.type,
    props.formik,
    callsToAction,
  ])

  useEffect(() => {
    if (!previewRef.current || !socialPreviewOptions) {
      return
    }

    embedSocialPreview(previewRef.current, socialPreviewOptions)
  }, [socialPreviewOptions])

  return <div ref={previewRef} />
}

const cloudinaryUrlWithCrops = (creativeMedia: Media | undefined | null) => {
  if (creativeMedia?.media_type !== 'image' && creativeMedia?.source_id) {
    return cloudinary.instance().video_url(creativeMedia.source_id, {
      transformations: [
        {
          quality: 'auto',
        },
      ],
    })
  }

  /** @type {cloudinary.Transformation.Options} */
  const options: {
    transformation: {
      width: number
      height?: number
      crop: string
      format: string
      quality: string
      x?: number
      y?: number
    }[]
  } = {
    transformation: [
      {
        width: 1280,
        crop: 'scale',
        format: 'jpg',
        quality: 'auto',
      },
    ],
  }
  // Grab the first crop.
  // TODO No idea what is proper here, may need like a whole bunch more for
  // context.
  const crop = creativeMedia?.image_crop_list?.[0]
  if (
    crop &&
    crop?.left_top?.length === 2 &&
    crop?.right_bottom?.length === 2
  ) {
    let transformation = {
      width: (crop?.right_bottom?.[0] || 0) - (crop?.left_top?.[0] || 0),
      height: (crop?.right_bottom?.[1] || 0) - (crop?.left_top?.[1] || 0),
      crop: 'crop',
      format: 'jpg',
      quality: 'auto',
      x: crop?.left_top?.[0] || 0,
      y: crop?.left_top?.[1] || 0,
    }

    options.transformation.unshift(transformation)
  }

  return creativeMedia?.source_id
    ? cloudinary.instance().url(creativeMedia?.source_id, options)
    : ''
}

export default connect(PaidAdSocialPreview)
